import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import InnerLayout from "../../../components/layouts/inner-layout";
import DynamicTable from "../../../components/tables/dynamic-table1";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import AccessDenied from "../../../components/helper-components/access-denied";
import emptyInvoice from "../../../assets/images/empty-invoice.png";
import AddButton from "../../../components/buttons/button";
import { DataContext } from "../../../components/layouts/main-layout";
import {
  warehouseGetIdAction,
  warehouseListWithCapacityAction,
} from "../../../redux/actions/inventory-action";

const WarehouseTable = () => {
  const { permissionFn } = useContext(DataContext);
  const hasReadAccess = permissionFn("warehouse_info")?.read === true;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [gateLoading, setGateLoading] = useState(true);
  const [gateError, setGateError] = useState(false);

  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    selectColumn: "",
    from: "",
    to: "",
    inputValue: "",
  });

  useEffect(() => {
    if (!hasReadAccess) return;
    dispatch(
      warehouseGetIdAction(
        {},
        (res) => {
          const payload = res?.data || {};
          if (payload?.wareHouseEnabled === false) {
            navigate("/under-dev/Warehouse Info", { replace: true });
            return;
          }
          if (payload?.multi === false) {
            navigate(`/warehouse/${payload?.pos_id}`, { replace: true });
            return;
          }
          setGateLoading(false);
        },
        () => {
          setGateError(true);
          setGateLoading(false);
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReadAccess]);

  useEffect(() => {
    if (!hasReadAccess || gateLoading || gateError) return;
    setLoading(true);
    dispatch(
      warehouseListWithCapacityAction(
        {},
        (res) => {
          setError(false);
          setLoading(false);
          setWarehouses(res?.data || []);
        },
        (res) => {
          setError(false);
          setLoading(false);
          setWarehouses([]);
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReadAccess, gateLoading, gateError, reload]);

  const filteredWarehouses = useMemo(() => {
    const query = filterData?.inputValue?.trim()?.toLowerCase();
    if (!query) return warehouses;
    return warehouses.filter(
      (item) =>
        item?.warehouse_code?.toLowerCase()?.includes(query) ||
        item?.warehouse_name?.toLowerCase()?.includes(query) ||
        item?.warehouse_location?.toLowerCase()?.includes(query)
    );
  }, [warehouses, filterData?.inputValue]);

  const paginatedWarehouses = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredWarehouses.slice(start, start + pageSize);
  }, [filteredWarehouses, currentPage, pageSize]);

  const tableBodyData = paginatedWarehouses?.map((item) => {
    return {
      id: item?.warehouse_id,
      warehouse_code: item?.warehouse_code ?? "--",
      warehouse_name: (
        <span className="flex items-center gap-5">
          {item?.warehouse_name ?? "--"}{" "}
          {item?.current && (
            <span
              style={{
                width: "fit-content",
                backgroundColor: "var(--success-color-opacity)",
              }}
              className="fs_12 text-027A48 fw_500 flex items-center gap-2 px-3 py-1 rounded-full"
            >
              {"Current"}
            </span>
          )}
        </span>
      ),
      warehouse_location: item?.warehouse_location ?? "--",
      tax_code: item?.tax_code ?? "--",
      total_bins: item?.total_bins ?? 0,
      capacity_display:
        item?.total_capacity > 0
          ? `${item?.used_capacity ?? 0}/${item?.total_capacity}`
          : "--",
    };
  });

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount: filteredWarehouses?.length,
    setCurrentPage,
    currentPage,
  };

  if (!hasReadAccess) {
    return (
      <InnerLayout title="Warehouse Info" backBtn={true}>
        <AccessDenied message="You don't have permission to view Warehouse Info." />
      </InnerLayout>
    );
  }

  if (gateLoading) {
    return (
      <InnerLayout title="Warehouse Info" backBtn={true}>
        <LoadingSpinner />
      </InnerLayout>
    );
  }

  if (gateError) {
    return (
      <InnerLayout title="Warehouse Info" backBtn={true}>
        <AccessDenied message="Unable to load warehouse settings. Please try again later." />
      </InnerLayout>
    );
  }

  return (
    <InnerLayout
      list={true}
      onclkFn={() => navigate("/create-warehouse")}
      title="Warehouse Info"
      module="warehouse_info"
      permissionFn={permissionFn}
      backBtn={true}
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <></>
      ) : warehouses?.length === 0 ? (
        <div className="flex flex-col justify-center items-center h-full">
          <div>
            <img src={emptyInvoice} alt="empty-invoice" />
            <h3 className="flex justify-center text-000000 fw_600 fs_18">
              No warehouses found{" "}
            </h3>
            <p className="flex justify-center text-667085 fw_400 mt-4 text-sm">
              Let’s start by adding your first warehouse.{" "}
            </p>
            {permissionFn("warehouse_info")?.create && (
              <div className="flex justify-center mt-3">
                <AddButton
                  text="Add New Warehouse"
                  onclkFn={() => navigate("/create-warehouse")}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <DynamicTable
          is_checkbox_req={false}
          id_name="warehouse_id"
          list={true}
          onclkFn={() => navigate("/create-warehouse")}
          filterSec={true}
          filter={false}
          date={false}
          permissionFn={permissionFn}
          module="warehouse_info"
          onClickItemFn={(id) => {
            const item = warehouses?.find((w) => w?.warehouse_id === id);
            navigate(`/warehouse/${id}`, {
              state: {
                warehouse_name: item?.warehouse_name,
                warehouse_code: item?.warehouse_code,
              },
            });
          }}
          reload={reload}
          setReload={setReload}
          loading={loading}
          setLoading={setLoading}
          label="Warehouse"
          paginationProps={paginationProps}
          filterData={filterData}
          setFilterData={setFilterData}
          tableHeading={tableHeading}
          tableBodyData={tableBodyData}
          placeholder="Search"
          newBtnTxt="Add New Warehouse"
          initialFilter={{
            sort_by: "",
            sort_order: "",
            selectColumn: "",
            from: "",
            to: "",
            inputValue: "",
          }}
        />
      )}
    </InnerLayout>
  );
};

export default WarehouseTable;

const tableHeading = [
  {
    label: "WAREHOUSE CODE",
    value: "warehouse_code",
    display: false,
    column: true,
  },
  {
    label: "WAREHOUSE NAME",
    value: "warehouse_name",
    display: false,
    column: true,
  },
  {
    label: "LOCATION",
    value: "warehouse_location",
    display: false,
    column: false,
  },
  {
    label: "TAX CODE",
    value: "tax_code",
    display: false,
    column: false,
  },
  {
    label: "TOTAL BINS",
    value: "total_bins",
    display: false,
    column: false,
  },
  {
    label: "CAPACITY USED",
    value: "capacity_display",
    display: false,
    column: false,
  },
];
